/**
 * Deployed-version identity for the running bundle.
 *
 * `APP_VERSION` is the semantic version from the committed `app/VERSION` file,
 * baked in at build time by Vite (`VITE_APP_VERSION`). Because it is read from a
 * tracked file — not a build arg — it is ALWAYS present, so the bundle is
 * self-identifying regardless of who built it or from which git mirror (PTSB build
 * from their own repo). This is the primary version shown to agents.
 *
 * `BUILD_COMMIT` (#430) is the optional, more precise per-build commit/tag,
 * injected by the Docker build (`VITE_BUILD_COMMIT` ← the immutable image tag). It
 * refines the display when present but is not required — an unstamped/local build
 * simply omits it. See docs/versioning.md.
 *
 * `DEPLOYED_VERSION` is the human-facing string: `v<APP_VERSION>` on its own, or
 * `v<APP_VERSION> (<commit>)` when a build commit was stamped. Surfaced as the PTSB
 * logo hover tooltip (see AgentBanner) AND logged to the console at startup, so
 * anyone can confirm exactly which version is deployed and on which route the
 * (Genesys-embedded) app loaded.
 */
export const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || '0.0.0';

export const BUILD_COMMIT =
  (import.meta.env.VITE_BUILD_COMMIT as string | undefined) || 'dev';

export const DEPLOYED_VERSION =
  BUILD_COMMIT && BUILD_COMMIT !== 'dev'
    ? `v${APP_VERSION} (${BUILD_COMMIT})`
    : `v${APP_VERSION}`;

/**
 * Logs the deployed build/commit and the URL the app loaded at. Called once at
 * SPA startup. Best-effort — never throws (guards non-browser/SSR contexts).
 */
export function logBuildInfo(): void {
  try {
    // eslint-disable-next-line no-console
    console.info(
      `[ptsb-agent] ${DEPLOYED_VERSION} — url ${window.location.href}`,
    );
  } catch {
    /* no window/console — ignore */
  }
}

/**
 * Logs the current in-app route (path + query + hash). Called on every
 * navigation by the {@link RouteLogger} so the console shows where the embedded
 * app is. Best-effort — never throws.
 */
export function logRoute(path: string): void {
  try {
    // eslint-disable-next-line no-console
    console.info(`[ptsb-agent] route ${path}`);
  } catch {
    /* no console — ignore */
  }
}
